import { formatEventLocation } from 'util/event';
import { Event } from 'services/api';

// Figma Active-Tickets 4ZdHY8bgfpcTL4REWmZ7sO
// Connected Dashboard — 9079:16202 (mobile), 9079:16277 (desktop)
export const FEED_COLORS = {
  surface: '#FFFFFF',
  border: '#E4E6EB',
  textPrimary: '#383838',
  textSecondary: '#5F6375',
  gold: '#FFE733',
  goldHover: '#F2D900',
  navy: '#242D56',
  navyHover: '#1B2344',
  inverse: '#FFFFFF',
  success: '#1E7A43',
  successTint: '#E8F6EE',
  liveBg: '#FFF2ED',
  liveText: '#C72626',
  chipBg: '#F1F1F3',
  line: '#E4E6EB',
  placeholder: '#F2D9BF',
} as const;

export const FEED_FONT = 'Poppins, sans-serif';

export const FEED_SHADOW =
  '0px 1px 3px rgba(0, 0, 0, 0.05), 0px 6px 20px rgba(13, 18, 51, 0.1)';

export const POSTER_SHADOW = '0px 10px 24px rgba(0, 0, 0, 0.35)';

export const RAIL_COLUMN = 44;
export const RAIL_GAP = 12;
export const DESKTOP_NOW_CARD = 276;
/** Upcoming rows keep their full-width size, then fill the column once it is narrower. */
export const DESKTOP_RAIL_MAX = 720;

const NBSP = '\u00A0';

export const feedLocation = (event: Event): string => {
  const setting = event.setting;
  if (!setting) return '';
  const labeled = formatEventLocation(setting);
  if (labeled) return labeled;
  if (typeof setting === 'object' && 'facilityRoomArea' in setting)
    return setting.facilityRoomArea || '';

  return '';
};

const clockParts = (
  time?: string,
): { time: string; meridiem: string } | null => {
  if (!time) return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(time.trim());
  if (!match) return null;
  let hours = Number(match[1]);
  const minutes = match[2];
  const explicit = /\b(AM|PM)\b/i.exec(time);
  let meridiem: string;
  if (explicit) {
    meridiem = explicit[1].toUpperCase();
  } else {
    meridiem = hours >= 12 ? 'PM' : 'AM';
    hours %= 12;
    if (hours === 0) hours = 12;
  }
  return { time: `${hours}:${minutes}`, meridiem };
};

export const formatFeedClockParts = clockParts;

/** `h:mm AM/PM` with a non-breaking space before the meridiem. */
export const formatFeedTimeRange = (start?: string, end?: string): string => {
  const startParts = clockParts(start);
  if (!startParts) return '';
  const startLabel = `${startParts.time}${NBSP}${startParts.meridiem}`;
  const endParts = clockParts(end);
  if (!endParts) return startLabel;
  return `${startLabel} – ${endParts.time}${NBSP}${endParts.meridiem}`;
};

export const formatCheckedInAt = (iso?: string): string => {
  if (!iso) return 'Checked in';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Checked in';
  const raw = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  const time = raw.replace(/\s*(AM|PM)$/i, `${NBSP}$1`);
  return `Checked in at ${time}`;
};

export const formatRailDay = (
  isoDate: string,
): { weekday: string; day: string } => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
  if (!match) return { weekday: '', day: '' };
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  return {
    weekday: date.toLocaleDateString('en-US', { weekday: 'short' }),
    day: String(date.getDate()),
  };
};
